import { lazy, Suspense, type ReactNode } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { ClerkProvider } from "@clerk/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { clerkPubKey, clerkProxyUrl, clerkEnabled } from "@/lib/clerk";
import { GoogleAuthBridge } from "@/components/auth/GoogleAuthBridge";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/hooks/auth-provider";
import { useAuth } from "@/hooks/use-auth";
import { SignInModal } from "@/components/SignInModal";
import Home from "@/pages/Home";
import NotFound from "@/pages/not-found";
import { InstallPrompt } from "@/components/InstallPrompt";
import { CookieNotice } from "@/components/CookieNotice";
import { useMetaPixelPageViews } from "@/lib/meta-pixel";

// Lazy-load non-critical routes so the home/landing bundle stays small.
// Each route's heavy dependencies (recharts, embla, day-picker, etc.) load
// on demand, keeping the critical path under the SEO weight target.
const SearchPage = lazy(() => import("@/pages/Search"));
const ShirtDetail = lazy(() => import("@/pages/ShirtDetail"));
const Browse = lazy(() => import("@/pages/Browse"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const Scan = lazy(() => import("@/pages/Scan"));
const ScanWashtag = lazy(() => import("@/pages/ScanWashtag"));
const PhotoScan = lazy(() => import("@/pages/PhotoScan"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TermsPage = lazy(() => import("@/pages/legal/Terms"));
const PrivacyPage = lazy(() => import("@/pages/legal/Privacy"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminNewShirts = lazy(() => import("@/pages/admin/NewShirts"));
const AdminPriceChanges = lazy(() => import("@/pages/admin/PriceChanges"));
const AdminStock = lazy(() => import("@/pages/admin/Stock"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const AccountSettings = lazy(() => import("@/pages/AccountSettings"));
const WatchlistPage = lazy(() => import("@/pages/Watchlist"));
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const BlogArticlePage = lazy(() => import("@/pages/blog/BlogArticle"));
const SsoCallback = lazy(() => import("@/pages/SsoCallback"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  useMetaPixelPageViews();
  return (
    <Suspense fallback={null}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/search" component={SearchPage} />
        <Route path="/browse" component={Browse} />
        <Route path="/sign-in" component={SignIn} />
        <Route path="/scan" component={Scan} />
        <Route path="/scan/photo" component={PhotoScan} />
        <Route path="/scan/washtag" component={ScanWashtag} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/legal/terms" component={TermsPage} />
        <Route path="/legal/privacy" component={PrivacyPage} />
        <Route path="/blog" component={BlogIndex} />
        <Route path="/blog/:slug" component={BlogArticlePage} />
        <Route path="/shirt/:slug" component={ShirtDetail} />
        <Route path="/admin/new-shirts" component={AdminNewShirts} />
        <Route path="/admin/price-changes" component={AdminPriceChanges} />
        <Route path="/admin/stock" component={AdminStock} />
        <Route path="/admin" component={Admin} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/account/settings" component={AccountSettings} />
        <Route path="/watchlist" component={WatchlistPage} />
        <Route path="/sso-callback/*?" component={SsoCallback} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function GlobalSignInModal() {
  const { signInModal, closeSignInModal } = useAuth();
  return (
    <SignInModal
      open={signInModal.open}
      onClose={closeSignInModal}
      reason={signInModal.reason}
    />
  );
}

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

// Clerk passes full paths to routerPush/routerReplace, but wouter's
// setLocation prepends the base — strip it to avoid doubling.
function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

/**
 * Mounts ClerkProvider (Google sign-in front-door) when a publishable key is
 * available; renders children unchanged otherwise so email/password auth
 * keeps working without Clerk. Must live inside <WouterRouter> so Clerk's
 * internal navigation uses wouter.
 */
function MaybeClerk({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  if (!clerkEnabled) return <>{children}</>;
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <GoogleAuthBridge />
      {children}
    </ClerkProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <TooltipProvider>
            <WouterRouter base={basePath}>
              <MaybeClerk>
                <Router />
                <GlobalSignInModal />
              </MaybeClerk>
            </WouterRouter>
            <Toaster />
            <InstallPrompt />
            <CookieNotice />
          </TooltipProvider>
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
